<template>
	<div class="characteristic-allowed-values-edit-form">
		<div
			v-if="hasValues"
			class="variants-block"
		>
			<div
				v-for="(allowedValue, index) in localValue"
				:key="uid + '_input_wrap_' + index"
				class="variants-block-item"
			>
				<VInput
					:ref="uid + '_input_' + index"
					v-model="localValue[index].name"
					placeholder="Введите название варианта"
					required
          :validate-method="(value) => validateName(value, index)"
					:disabled="allowedValue.id !== undefined"
				/>

				<div class="buttons-wrap">
					<VButton
						v-if="allowedValue.id === undefined"
						theme="primary"
						:disabled="isSaveButtonDisabled(index)"
						@click="saveAllowedValue(index)"
						material="save"
					/>

					<VButton
						v-else
						:disabled="loadingIndexes.has(index)"
						@click="deleteAllowedValue(index)"
						material="delete"
					/>
				</div>
			</div>
		</div>
		<div
			v-else
			class="empty-block"
		>
			Список пуст
		</div>

		<div class="buttons-block">
			<VButton
				material="add"
				@click="addAllowedValue"
			>
				Добавить вариант
			</VButton>
		</div>
	</div>
</template>

<style lang="scss" src="./CharacteristicAllowedValuesEdit.scss" />

<script lang="ts">
import CharacteristicAllowedValue         from '@/domains/Characteristics/CharacteristicAllowedValue';
import CharacteristicAllowedValuesService from '@/domains/Characteristics/CharacteristicAllowedValuesService';
import Utils                              from '@/core/utils/Utils';

import uniqueIdMixin from '@/lib/mixins/component-unique-id';

export default defineComponent({
	name: 'CharacteristicAllowedValuesEdit',

	mixins: [uniqueIdMixin],

	props: {
		value: {
			type: Array as PropType<CharacteristicAllowedValue[]>,
			default: () => [],
		},
		characteristicId: {
			type: Number,
			default: null,
		},
	},

	emits: ['allowed-value:created', 'allowed-value:deleted'],

	data() {
		return {
			localValue: Utils.cloneDeep(this.value) as CharacteristicAllowedValue[],
			/** Индексы строк, по которым идёт запрос — блокирует конкретную кнопку, а не форму. */
			loadingIndexes: new Set<number>(),
		};
	},

	computed: {
		hasValues(): boolean {
			return this.localValue.length > 0;
		},
	},

	methods: {
		addAllowedValue(): void {
			const localValue = Utils.cloneDeep(this.localValue);
			localValue.push(new CharacteristicAllowedValue());
			this.localValue = localValue;
		},

		isSaveButtonDisabled(index: number): boolean {
			const ref = this.$refs[`${this.uid}_input_${index}`];
			if (!ref) return true;
			const input = (ref as any)[0];
			return !input?.validation.isValid || input?.validation.loading;
		},

		async saveAllowedValue(index: number): Promise<void> {
			if (this.loadingIndexes.has(index)) return;

			const input = (this.$refs[`${this.uid}_input_${index}`] as any)?.[0];
			if (!input) return;

			input.validateForce();
			if (!input?.validation.isValid) return;

			this.loadingIndexes = new Set(this.loadingIndexes).add(index);

			try {
				const success = await CharacteristicAllowedValuesService.add(
					this.characteristicId,
					this.localValue[index].name!,
				);

				if (success) {
					const response = await CharacteristicAllowedValuesService.getList({
						characteristicId: this.characteristicId,
					});

					const newValue = response.data.find(
						v => v.name === this.localValue[index].name,
					);

					if (newValue) {
						const localValue = Utils.cloneDeep(this.localValue);
						localValue[index] = newValue;
						this.localValue = localValue;
					}

					this.$emit('allowed-value:created');
				}
			} finally {
				const next = new Set(this.loadingIndexes);
				next.delete(index);
				this.loadingIndexes = next;
			}
		},

		async deleteAllowedValue(index: number): Promise<void> {
			if (this.loadingIndexes.has(index)) return;

			this.loadingIndexes = new Set(this.loadingIndexes).add(index);

			try {
				const success = await CharacteristicAllowedValuesService.delete(
					this.localValue[index].id!,
				);

				if (success) {
					const localValue = Utils.cloneDeep(this.localValue);
					localValue.splice(index, 1);
					// после splice индексы сдвинулись — сбрасываем loading
					this.loadingIndexes = new Set();
					this.localValue = localValue;

					this.$emit('allowed-value:deleted');
				}
			} finally {
				const next = new Set(this.loadingIndexes);
				next.delete(index);
				this.loadingIndexes = next;
			}
		},

		validateName(value: string, currentIndex): string | null {
      // Уже сохранённые значения (id есть) не должны валидироваться повторно —
      // они пришли с сервера и не редактируются (input disabled), а форма
      // родительской карточки прогоняет валидацию всех вложенных контролов
      // при сабмите. Дубль на сервере всё равно поймает unique-индекс в БД.
      const currentAllowedValue = this.localValue[currentIndex];
      if (currentAllowedValue?.id !== undefined) {
        return null;
      }

      // Проверяем совпадение имени с ДРУГИМИ (не собой) элементами списка
      const isDuplicate = this.localValue.some(
        (allowedValue, index) =>
          index !== currentIndex
          && allowedValue.name === value
          && allowedValue.id !== undefined,
      );

      if (isDuplicate) {
        return 'Такое значение уже существует';
      }

      return null;
		},
	},
});
</script>
